import { useCallback, useEffect, useReducer, useRef } from "react";
import { useAuth } from "../provider/AuthProvider";
import ClientsService from "../services/ClientsService/ClientsService";
import {
  findKeyByValue,
  normalizeClientProperties,
  normalizeClientSaveResponseProperties,
  normalizeVehicleProperties,
} from "../utils/NormalizationUtil";
import VehicleService from "../services/VehicleService/VehicleService";
import PolicyService from "../services/PolicyService/PolicyService.ts";
import InsuranceService from "../services/InsuranceService/InsuranceService";
import moment from "moment";
import { PolicyFullObject, PolicyValuesObject } from "../types/insurance";
import {
  buildInstallment,
  calcStepMonths,
} from "../utils/InstallmentsUtils.tsx";

type PolicyFormState = {
  policyData: PolicyFullObject;
  policyValues: PolicyValuesObject[];
  baseInsuranceCompanies: any;
  insuranceCompanies: any;
  policyTypes: any;
  offices: any;
  agentOptions: any;
  vehicleTypes: any;
  vehicleUsages: any;
  vehicleBrands: any;
  errors: string[];
  successMessage: string;
  warnings: string[];
  payPlaces: any;
  agents: any;
  stickers: any[];
  greenCards: any[];
  paymentAccounts: any[];
  /**
   * True only while we have loaded installments from the backend for the
   * currently-displayed policy.  Reset to false on RESET_STATE so that a
   * brand-new policy always starts with an empty grid.
   */
  hasBackendInstallments: boolean;
};

type Action =
  | { type: "SET_POLICY_DATA"; payload: Partial<PolicyFullObject> }
  | { type: "SET_POLICY_VALUES"; payload: PolicyValuesObject[] }
  | { type: "SET_INSURANCE_COMPANIES"; payload: any }
  | { type: "SET_BASE_INSURANCE_COMPANIES"; payload: any }
  | { type: "SET_POLICY_TYPES"; payload: any }
  | { type: "SET_OFFICES"; payload: any }
  | { type: "SET_AGENT_OPTIONS"; payload: any }
  | { type: "SET_VEHICLE_TYPES"; payload: any }
  | { type: "SET_VEHICLE_USAGES"; payload: any }
  | { type: "SET_VEHICLE_BRANDS"; payload: any }
  | { type: "SET_ERRORS"; payload: string[] }
  | { type: "SET_WARNINGS"; payload: string[] }
  | { type: "SET_SUCCESS_MESSAGE"; payload: string }
  | { type: "SET_PAY_PLACES"; payload: any }
  | { type: "SET_AGENTS"; payload: any }
  | { type: "SET_STICKERS"; payload: any[] }
  | { type: "SET_GREEN_CARDS"; payload: any[] }
  | { type: "SET_PAYMENT_ACCOUNTS"; payload: any[] }
  | { type: "SET_HAS_BACKEND_INSTALLMENTS"; payload: boolean }
  | { type: "RESET_STATE" }
  /**
   * Used exclusively after a successful search to hydrate both the policy
   * header data and its installments in a single, atomic dispatch - ensuring
   * the effect that watches policyData.policyId never fires for this scenario.
   */
  | {
      type: "LOAD_EXISTING_POLICY";
      payload: {
        policyData: PolicyFullObject;
        policyValues: PolicyValuesObject[];
      };
    };

const initialPolicyData: PolicyFullObject = {
  policyId: "0",
  policyNumber: "",
  insuranceCompanyId: "",
  insuranceCompany: "",
  policyTypeId: 5,
  policyType: "1",
  agentId: "",
  agent: "",
  divisionId: "",
  divisionName: "",
  policyStatus: "1",
  insuredSum: "0",
  policySum: 0,
  contractDate: moment().format("DD/MM/YYYY"),
  startDate: moment().add(1, "days").format("DD/MM/YYYY"),
  endDate: moment().add(1, "year").subtract(1, "days").format("DD/MM/YYYY"),
  reportDate: moment().format("DD/MM/YYYY"),
  installmentsCount: 0,
  policyAdditionalData: "",
  clientId: "",
  carId: "",
  gfSum: 0,
  agentCommissionPercentage: "",
  officeCommissionPercentage: "",
  vehicleRegistrationNumber: "",
  vehicleCertificate: "",
  chassisNumber: "",
  vehicleBrand: "",
  engineCapacity: "",
  vehicleUsage: "",
  manufactureYear: "",
  vehicleOtherData: "",
  insuranceClientPin: "",
  insuranceClientName: "",
  insuranceClientPhone: "",
  insuranceClientAddress: "",
  clientOtherData: "",
  vehicleModel: "",
  zeroCommission: 0,
  vehicleVolume: "",
  vehicleType: "",
  additionalCount: "",
  twoPercentAmount: 0,
  annexes: [],
};

const initialState: PolicyFormState = {
  policyData: initialPolicyData,
  policyValues: [],
  baseInsuranceCompanies: {},
  insuranceCompanies: {},
  policyTypes: {},
  offices: {},
  agentOptions: {},
  vehicleTypes: {},
  vehicleBrands: {},
  errors: [],
  successMessage: "",
  warnings: [],
  payPlaces: {},
  agents: {},
  stickers: [],
  greenCards: [],
  paymentAccounts: [],
  hasBackendInstallments: false,
  vehicleUsages: 1,
};

function reducer(state: PolicyFormState, action: Action): PolicyFormState {
  switch (action.type) {
    case "SET_POLICY_DATA":
      return {
        ...state,
        policyData: { ...state.policyData, ...action.payload },
      };
    case "SET_POLICY_VALUES":
      return { ...state, policyValues: action.payload };
    case "SET_INSURANCE_COMPANIES":
      return { ...state, insuranceCompanies: action.payload };
    case "SET_BASE_INSURANCE_COMPANIES":
      return { ...state, baseInsuranceCompanies: action.payload };
    case "SET_POLICY_TYPES":
      return { ...state, policyTypes: action.payload };
    case "SET_OFFICES":
      return { ...state, offices: action.payload };
    case "SET_AGENT_OPTIONS":
      return { ...state, agentOptions: action.payload };
    case "SET_VEHICLE_TYPES":
      return { ...state, vehicleTypes: action.payload };
    case "SET_VEHICLE_USAGES":
      return { ...state, vehicleUsages: action.payload };
    case "SET_VEHICLE_BRANDS":
      return { ...state, vehicleBrands: action.payload };
    case "SET_PAY_PLACES":
      return { ...state, payPlaces: action.payload };
    case "SET_AGENTS":
      return { ...state, agents: action.payload };
    case "SET_STICKERS":
      return { ...state, stickers: action.payload };
    case "SET_GREEN_CARDS":
      return { ...state, greenCards: action.payload };
    case "SET_PAYMENT_ACCOUNTS":
      return { ...state, paymentAccounts: action.payload };
    case "SET_ERRORS":
      return { ...state, errors: action.payload };
    case "SET_WARNINGS":
      return { ...state, warnings: action.payload };
    case "SET_SUCCESS_MESSAGE":
      return { ...state, successMessage: action.payload };
    case "SET_HAS_BACKEND_INSTALLMENTS":
      return { ...state, hasBackendInstallments: action.payload };

    // Atomic load of a searched policy + its installments.
    // By setting both in one dispatch we never expose an intermediate state
    // where policyId changed but policyValues haven't arrived yet.
    case "LOAD_EXISTING_POLICY":
      return {
        ...state,
        policyData: action.payload.policyData,
        policyValues: action.payload.policyValues,
        hasBackendInstallments: action.payload.policyValues.length > 0,
      };

    case "RESET_STATE":
      return {
        ...initialState,
        // Keep reference data so the user doesn't have to wait for dropdowns again.
        insuranceCompanies: state.insuranceCompanies,
        baseInsuranceCompanies: state.baseInsuranceCompanies,
        policyTypes: state.policyTypes,
        offices: state.offices,
        agentOptions: state.agentOptions,
        vehicleTypes: state.vehicleTypes,
        vehicleUsages: state.vehicleUsages,
        vehicleBrands: state.vehicleBrands,
      };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Auto-dismiss helper
// ---------------------------------------------------------------------------

const useAutoDismiss = (
  value: string | string[],
  clear: () => void,
  delay = 5000
) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasValue = Array.isArray(value) ? value.length > 0 : !!value;

  useEffect(() => {
    if (!hasValue) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(clear, delay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const usePolicyForm = () => {
  const { jwt, headOff } = useAuth();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { policyData, policyValues } = state;

  // ── helpers ──────────────────────────────────────────────────────────────

  const setError = (msg: string) =>
    dispatch({ type: "SET_ERRORS", payload: [msg] });
  const setSuccess = (msg: string) =>
    dispatch({ type: "SET_SUCCESS_MESSAGE", payload: msg });
  const setWarning = (msg: string) =>
    dispatch({ type: "SET_WARNINGS", payload: [msg] });
  const setPolicyField = (payload: Partial<PolicyFullObject>) =>
    dispatch({ type: "SET_POLICY_DATA", payload });

  useAutoDismiss(state.errors, () =>
    dispatch({ type: "SET_ERRORS", payload: [] })
  );
  useAutoDismiss(state.successMessage, () =>
    dispatch({ type: "SET_SUCCESS_MESSAGE", payload: "" })
  );
  useAutoDismiss(state.warnings, () =>
    dispatch({ type: "SET_WARNINGS", payload: [] })
  );

  // Always-fresh ref so callbacks that capture policyData don't go stale.
  const policyDataRef = useRef(policyData);
  policyDataRef.current = policyData;

  // ── reference-data load (once on mount) ──────────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        const [
          insuranceCompaniesData,
          baseInsuranceCompanies,
          policyTypesData,
          officesData,
          agentOptionsData,
          vehicleTypesData,
          vehicleUsagesData,
          vehicleBrandsData,
        ] = await Promise.all([
          InsuranceService.getAllInsuranceCompanies(jwt),
          InsuranceService.getBaseInsuranceCompanies(jwt),
          InsuranceService.getAllPolicyTypes(jwt),
          InsuranceService.getAllOffices(jwt),
          InsuranceService.getAllAgents(jwt),
          VehicleService.getVehicleTypes(jwt),
          VehicleService.getVehicleUsages(jwt),
          VehicleService.getVehicleBrands(jwt),
        ]);
        dispatch({
          type: "SET_INSURANCE_COMPANIES",
          payload: insuranceCompaniesData,
        });
        dispatch({
          type: "SET_BASE_INSURANCE_COMPANIES",
          payload: baseInsuranceCompanies,
        });
        dispatch({ type: "SET_POLICY_TYPES", payload: policyTypesData });
        dispatch({ type: "SET_OFFICES", payload: officesData });
        dispatch({ type: "SET_AGENT_OPTIONS", payload: agentOptionsData });
        dispatch({ type: "SET_VEHICLE_TYPES", payload: vehicleTypesData });
        dispatch({ type: "SET_VEHICLE_USAGES", payload: vehicleUsagesData });
        dispatch({ type: "SET_VEHICLE_BRANDS", payload: vehicleBrandsData });
      } catch (e: any) {
        setError("Грешка при зареждане на данни: " + e.message);
      }
    };
    load();
  }, [jwt, headOff]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── per-company side-data (stickers, green cards, payment accounts) ──────

  useEffect(() => {
    if (!policyData.insuranceCompanyId) return;
    let cancelled = false;
    const { insuranceCompanyId } = policyData;

    Promise.all([
      PolicyService.fetchPayPlaces(jwt),
      InsuranceService.getAllAgents(jwt),
      PolicyService.fetchStickersByCompanyId(insuranceCompanyId, jwt),
      PolicyService.fetchGreenCardsByCompanyId(insuranceCompanyId, jwt),
      PolicyService.fetchPaymentAccountsByCompanyId(insuranceCompanyId, jwt),
    ])
      .then(([payPlaces, agents, stickers, greenCards, paymentAccounts]) => {
        if (cancelled) return;
        dispatch({ type: "SET_PAY_PLACES", payload: payPlaces });
        dispatch({ type: "SET_AGENTS", payload: agents });
        dispatch({ type: "SET_STICKERS", payload: ["", ...stickers] });
        dispatch({ type: "SET_GREEN_CARDS", payload: ["", ...greenCards] });
        dispatch({
          type: "SET_PAYMENT_ACCOUNTS",
          payload: ["", ...paymentAccounts],
        });
      })
      .catch((e) =>
        setError("Грешка при зареждане на данни за полица: " + e.message)
      );

    return () => {
      cancelled = true;
    };
  }, [policyData.insuranceCompanyId, jwt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── lazy client hydration (when clientId arrives from search result) ──────

  useEffect(() => {
    if (!policyData.clientId) return;
    let cancelled = false;

    ClientsService.getClientById(jwt, policyData.clientId)
      .then((data) => {
        if (cancelled) return;
        const { clientId, ...rest } = normalizeClientProperties(data as object);
        setPolicyField(rest);
      })
      .catch((e) =>
        setError("Грешка при зареждането на данни за застрахован: " + e.message)
      );

    return () => {
      cancelled = true;
    };
  }, [policyData.clientId, jwt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── lazy vehicle hydration (when carId arrives from search result) ────────
  useEffect(() => {
    if (!policyData.carId) return;
    let cancelled = false;

    VehicleService.getVehicleById(jwt, policyData.carId)
      .then((data) => {
        if (cancelled) return;
        const { carId, ...rest } = normalizeVehicleProperties(data);

        setPolicyField({
          ...rest,
          vehicleUsage: findKeyByValue(
            state.vehicleUsages,
            rest.vehicleUsage ?? ""
          ),
          vehicleType: findKeyByValue(
            state.vehicleTypes,
            rest.vehicleType ?? ""
          ),
        });
      })
      .catch((e) =>
        setError("Грешка при зареждането на данни за МПС: " + e.message)
      );

    return () => {
      cancelled = true;
    };
  }, [policyData.carId, state.vehicleUsages, state.vehicleTypes, jwt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── date cascade ─────────────────────────────────────────────────────────
  //
  // skipDateCalc is set to true by handleSearchPolicy so that loading a
  // saved policy doesn't overwrite the stored dates.

  const skipDateCalc = useRef(false);

  useEffect(() => {
    if (!policyData.contractDate) return;
    if (skipDateCalc.current) return;

    const startDate = moment(policyData.contractDate, "DD/MM/YYYY")
      .add(1, "days")
      .format("DD/MM/YYYY");
    const reportDate = moment(policyData.contractDate, "DD/MM/YYYY").format(
      "DD/MM/YYYY"
    );
    setPolicyField({ startDate, reportDate });
  }, [policyData.contractDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!policyData.startDate) return;
    if (skipDateCalc.current) {
      skipDateCalc.current = false;
      return;
    }

    const endDate = moment(policyData.startDate, "DD/MM/YYYY")
      .add(1, "year")
      .subtract(1, "days")
      .format("DD/MM/YYYY");
    setPolicyField({ endDate });
  }, [policyData.startDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── change handlers ───────────────────────────────────────────────────────

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setPolicyField({ [e.target.name]: e.target.value }),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleIdentifierChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setPolicyField({ insuranceClientPin: e.target.value }),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const setPolicyData = useCallback(
    (value: React.SetStateAction<PolicyFullObject>) => {
      setPolicyField(typeof value === "function" ? value(policyData) : value);
    },
    [policyData] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── data loaders ──────────────────────────────────────────────────────────

  const loadClientDataByPin = useCallback(async () => {
    if (!policyData.insuranceClientPin) return;
    try {
      const data = await ClientsService.getClient(
        jwt,
        policyData.insuranceClientPin
      );
      setPolicyField(normalizeClientProperties(data as object));
    } catch (e: any) {
      setError("Грешка при зареждане на данни за клиент: " + e.message);
    }
  }, [jwt, policyData.insuranceClientPin]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCarDataById = useCallback(
    async (regNumber?: string, certificate?: string) => {
      const reg = regNumber ?? policyData.vehicleRegistrationNumber;
      const cert = certificate ?? policyData.vehicleCertificate;

      if (!reg && !cert) return;

      try {
        const data = await VehicleService.getVehicle(reg, cert, jwt);

        if (!Object.values(data).length) {
          setWarning(`Няма намерени данни за МПС с № ${reg}`);
        } else {
          setPolicyField(normalizeVehicleProperties(data));
        }
      } catch (e: any) {
        setError("Грешка при записване на данни за МПС: " + e.message);
      }
    },
    [jwt, policyData.vehicleRegistrationNumber, policyData.vehicleCertificate] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── save helpers ──────────────────────────────────────────────────────────

  /**
   * Persists the policy header.
   *
   * KEY CHANGE: we no longer write the returned policyId back to state here.
   * Writing it back caused the `useEffect([policyData.policyId])` that loads
   * installments to fire immediately after saving - a race condition.
   * The caller (handleSaveAll) receives the id and forwards it directly to
   * savePolicyInstallments without touching state.
   */
  const savePolicyData = useCallback(async (): Promise<string | null> => {
    try {
      const result = await PolicyService.savePolicyData(
        { ...policyData, installmentsCount: policyValues.length },
        jwt
      );
      // Return the persisted id to the caller; do NOT dispatch it to state.
      return result.policyId ?? policyData.policyId ?? null;
    } catch (e: any) {
      setError("Възникна грешка при записа на полицата: " + e.message);
      return null;
    }
  }, [jwt, policyData, policyValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePolicyInstallments = useCallback(
    async (policyIdOverride?: string) => {
      const idToUse = policyIdOverride ?? policyData.policyId;
      if (!idToUse || idToUse === "0") {
        setError("Няма запазена полица - първо запишете полицата");
        return;
      }
      try {
        const result = await PolicyService.savePolicyInstallments(
          idToUse,
          policyValues,
          jwt
        );
        if (result.message === "Успешен запис") {
          setSuccess(`Успешен запис по полица №${result.policyId}`);
        }
      } catch (e: any) {
        setError("Възникна грешка при записа на падежите: " + e.message);
      }
    },
    [jwt, policyData, policyValues] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const saveClientData = useCallback(async () => {
    if (!policyData.insuranceClientPin) {
      setError("Моля, въведете ЕГН или Булстат за клиент");
      return;
    }
    try {
      const result = await ClientsService.saveClient(
        jwt,
        normalizeClientProperties(
          {
            clientId: policyData.clientId ?? 0,
            insuranceClientPin: policyData.insuranceClientPin,
            insuranceClientName: policyData.insuranceClientName,
            insuranceClientAddress: policyData.insuranceClientAddress,
            insuranceClientPhone: policyData.insuranceClientPhone,
            otherData: policyData.clientOtherData,
          },
          true
        )
      );
      if (result && typeof result === "object") {
        setSuccess("Данните на клиента са записани успешно");
        const normalized = normalizeClientSaveResponseProperties(
          result as object
        );
        setPolicyField({ clientId: normalized.clientId });
      } else {
        throw new Error("Грешни получени данни при запис на клиент");
      }
    } catch (e: any) {
      setError(
        "Възникна грешка при записа на данните на клиента: " + e.message
      );
    }
  }, [
    jwt,
    policyData.clientId,
    policyData.insuranceClientPin,
    policyData.insuranceClientName,
    policyData.insuranceClientAddress,
    policyData.insuranceClientPhone,
    policyData.clientOtherData,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveVehicleData = useCallback(async () => {
    if (!policyData.vehicleRegistrationNumber) {
      setError("Моля, въведете регистрационен номер на МПС");
      return;
    }
    try {
      const result = await VehicleService.saveVehicle(
        jwt,
        normalizeVehicleProperties(
          {
            vehicleRegistrationNumber: policyData.vehicleRegistrationNumber,
            carId: policyData.carId,
            vehicleBrand: policyData.vehicleBrand,
            vehicleModel: policyData.vehicleModel,
            vehicleVolume: policyData.vehicleVolume,
            engineCapacity: policyData.engineCapacity,
            vehicleType: policyData.vehicleType,
            vehicleCertificate: policyData.vehicleCertificate,
            manufactureYear: policyData.manufactureYear,
            vehicleUsage: policyData.vehicleUsage,
            chassisNumber: policyData.chassisNumber,
          },
          true
        )
      );
      if (result && typeof result === "object") {
        setSuccess("Данните за МПС са записани успешно");
        setPolicyField({ carId: result.Car_ID });
      } else {
        throw new Error("Грешни получени данни при запис на МПС");
      }
    } catch (e: any) {
      setError("Възникна грешка при записа на данните за МПС: " + e.message);
    }
  }, [
    jwt,
    policyData.vehicleRegistrationNumber,
    policyData.carId,
    policyData.vehicleBrand,
    policyData.vehicleModel,
    policyData.vehicleVolume,
    policyData.engineCapacity,
    policyData.vehicleType,
    policyData.vehicleCertificate,
    policyData.manufactureYear,
    policyData.vehicleUsage,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── search / load existing policy ─────────────────────────────────────────
  //
  // All data for an existing policy (header + installments) is fetched here
  // and committed in a single LOAD_EXISTING_POLICY dispatch so that no
  // intermediate state is ever visible and no other effect is triggered.

  const handleSearchPolicy = useCallback(
    async (policyNumber: string) => {
      try {
        const data = await PolicyService.fetchPolicyByPolicyNumber(
          policyNumber,
          jwt,
          policyData.insuranceCompanyId,
          policyData.policyTypeId
        );

        if (data.policyId === "0" || !data.policyId) {
          setWarning(`Няма намерена полица с номер ${policyNumber}`);
          return;
        }

        // Suppress the date-cascade effects for this load.
        skipDateCalc.current = true;

        const contractDate = data.contractDate || moment().format("DD/MM/YYYY");
        const startDate =
          data.startDate ||
          moment(contractDate, "DD/MM/YYYY")
            .add(1, "days")
            .format("DD/MM/YYYY");
        const endDate =
          data.endDate ||
          moment(startDate, "DD/MM/YYYY")
            .add(1, "year")
            .subtract(1, "days")
            .format("DD/MM/YYYY");
        const reportDate = data.reportDate || contractDate;

        const normalizedPolicyData: PolicyFullObject = {
          ...data,
          contractDate,
          startDate,
          endDate,
          reportDate,
        };

        // Fetch installments in the same "transaction" so we never have a
        // window where policyId changed but policyValues is still stale.
        const installments = await PolicyService.fetchValuesByPolicyId(
          data.policyId,
          jwt
        );

        dispatch({
          type: "LOAD_EXISTING_POLICY",
          payload: {
            policyData: normalizedPolicyData,
            policyValues: installments,
          },
        });
      } catch (e: any) {
        setError("Грешка при зареждане на полица: " + e.message);
      }
    },
    [jwt, policyData.insuranceCompanyId, policyData.policyTypeId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── reset ─────────────────────────────────────────────────────────────────

  const resetForm = useCallback(() => {
    dispatch({ type: "RESET_STATE" });
  }, []);

  // ── installment grid helpers ───────────────────────────────────────────────

  const parseMoney = (value: any) => {
    return (
      Number(
        String(value ?? "")
          .replace(/\s/g, "")
          .replace(",", ".")
      ) || 0
    );
  };

  const agentId = Number(policyData.agentId);

  const addRowsBasedOnInstallments = useCallback(
    (count: number) => {
      const { startDate, endDate, policyId, policySum, gfSum, contractDate } =
        policyDataRef.current;

      const start = moment(startDate, "DD/MM/YYYY");
      const end = moment(endDate, "DD/MM/YYYY");
      const contractDateMoment = moment(contractDate, "DD/MM/YYYY");

      if (!start.isValid() || !end.isValid() || !start.isBefore(end)) {
        setError("Невалидна дата");
        return;
      }

      const stepMonths = calcStepMonths(start, end, count);
      const hasExistingValues = state.hasBackendInstallments;

      if (hasExistingValues) {
        if (count > policyValues.length) {
          const newRows = Array.from(
            { length: count - policyValues.length },
            (_, i) => {
              const index = policyValues.length + i;
              const date =
                index === 0
                  ? contractDateMoment.clone()
                  : start.clone().add(stepMonths * index, "months");

              return {
                policyId: Number(policyId) || 0,
                installmentDate: date.format("DD/MM/YYYY"),
                reportDate: date.format("DD/MM/YYYY"),
                accountNumber: "",
                installmentAmount: 0,
                installmentSequence: index + 1,
                paymentLocation: "0",
                sticker: "",
                paidToAgent: 0,
                stickerAmount: 0,
                twoPercentAmount: 0,
                agentId,
                greenCardCertificate: "",
              } as PolicyValuesObject;
            }
          );
          dispatch({
            type: "SET_POLICY_VALUES",
            payload: [...policyValues, ...newRows],
          });
        } else if (count < policyValues.length) {
          const trimmed = policyValues
            .slice(0, count)
            .map((row, i) => ({ ...row, installmentSequence: i + 1 }));
          dispatch({ type: "SET_POLICY_VALUES", payload: trimmed });
        }
      } else {
        const base = Number(policySum) / count;
        const extra = Number(gfSum || 0);
        const rows = Array.from({ length: count }, (_, i) =>
          buildInstallment(
            i,
            i === 0 ? contractDateMoment : start,
            stepMonths,
            Number(policyId),
            base,
            extra,
            agentId,
            i === 0 ? contractDate : undefined
          )
        );
        dispatch({ type: "SET_POLICY_VALUES", payload: rows });
      }

      setPolicyField({ installmentsCount: count });
    },
    [policyValues, agentId, state.hasBackendInstallments] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const addSingleRow = useCallback(() => {
    const { startDate, endDate, contractDate, policyId, policySum, gfSum } =
      policyDataRef.current;
    const contractDateMoment = moment(contractDate, "DD/MM/YYYY");

    if (!startDate || !endDate) {
      setError("Моля, изберете начална и крайна дата");
      return;
    }
    const start = moment(startDate, "DD/MM/YYYY");
    const end = moment(endDate, "DD/MM/YYYY");

    if (!start.isValid() || !end.isValid() || !start.isBefore(end)) {
      setError("Невалидна дата");
      return;
    }

    const newCount = policyValues.length + 1;

    if (!state.hasBackendInstallments) {
      const stepMonths = calcStepMonths(start, end, newCount);
      const base = Number(policySum) / newCount;
      const extra = Number(gfSum || 0);
      const rows = Array.from({ length: newCount }, (_, i) =>
        buildInstallment(
          i,
          i === 0 ? moment(policyData.reportDate, "DD/MM/YYYY") : start,
          stepMonths,
          Number(policyId),
          base,
          extra,
          agentId,
          i === 0 ? policyData.reportDate : undefined
        )
      );
      dispatch({ type: "SET_POLICY_VALUES", payload: rows });
    } else {
      const stepMonths = calcStepMonths(start, end, newCount);
      const date =
        policyValues.length === 0
          ? contractDateMoment.clone()
          : start.clone().add(stepMonths * policyValues.length, "months");

      const newRow: PolicyValuesObject = {
        policyId: Number(policyId) || 0,
        installmentDate: date.format("DD/MM/YYYY"),
        reportDate: date.format("DD/MM/YYYY"),
        accountNumber: "",
        installmentAmount: 0,
        installmentSequence: newCount,
        paymentLocation: "0",
        sticker: "",
        paidToAgent: 0,
        stickerAmount: 0,
        twoPercentAmount: 0,
        agentId,
        greenCardCertificate: "",
      };
      dispatch({
        type: "SET_POLICY_VALUES",
        payload: [...policyValues, newRow],
      });
    }

    setPolicyField({ installmentsCount: newCount });
  }, [policyValues, agentId, state.hasBackendInstallments]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRowDelete = useCallback(
    (rowIndex: number) => {
      if (
        rowIndex === undefined ||
        rowIndex < 0 ||
        rowIndex >= policyValues.length
      ) {
        setError("Моля, маркирайте валиден ред за премахване");
        return;
      }

      const { startDate, endDate } = policyData;
      const start = moment(startDate, "DD/MM/YYYY");
      const end = moment(endDate, "DD/MM/YYYY");

      if (!start.isValid() || !end.isValid() || !start.isBefore(end)) {
        setError("Невалидна дата");
        return;
      }

      const updated = policyValues
        .filter((_, i) => i !== rowIndex)
        .map((row, i) => ({ ...row, installmentSequence: i + 1 }));

      dispatch({ type: "SET_POLICY_VALUES", payload: updated });
      setPolicyField({ installmentsCount: updated.length });
    },
    [policyValues, policyData] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── sync agentId to installment rows that have no agent set ──────────────
  useEffect(() => {
    if (!policyData.agentId || policyValues.length === 0) return;

    const newAgentId = Number(policyData.agentId);
    const hasUnassignedRows = policyValues.some(
      (row) => Number(row.agentId) === 0
    );
    if (!hasUnassignedRows) return;

    dispatch({
      type: "SET_POLICY_VALUES",
      payload: policyValues.map((row) =>
        Number(row.agentId) === 0 ? { ...row, agentId: newAgentId } : row
      ),
    });
  }, [policyData.agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updatePolicyValueRow = useCallback(
    ({
      value,
      rowIndex,
      columnId,
    }: {
      value: any;
      rowIndex: number;
      columnId: keyof PolicyValuesObject;
    }) => {
      const updated = [...policyValues];

      const updatedRow: PolicyValuesObject = {
        ...updated[rowIndex],
        [columnId]: value,
      };

      if (columnId === "installmentAmount") {
        if (rowIndex === 0) {
          const installmentAmount = parseMoney(value);
          updatedRow.twoPercentAmount = Number(
            (
              (installmentAmount - (policyData.gfSum ? policyData.gfSum : 0)) *
              0.02
            ).toFixed(2)
          );
        } else {
          const installmentAmount = parseMoney(value);
          updatedRow.twoPercentAmount = Number(
            (installmentAmount * 0.02).toFixed(2)
          );
        }
      }

      updated[rowIndex] = updatedRow;
      dispatch({ type: "SET_POLICY_VALUES", payload: updated });
    },
    [policyValues]
  );

  // ── public API ────────────────────────────────────────────────────────────

  return {
    ...state,
    addRowsBasedOnInstallments,
    addSingleRow,
    setPolicyData,
    handleChange,
    updatePolicyValueRow,
    handleIdentifierChange,
    loadClientDataByPin,
    loadCarDataById,
    savePolicyData,
    savePolicyInstallments,
    saveClientData,
    saveVehicleData,
    handleSearchPolicy,
    resetForm,
    onRowDelete,
  };
};

export default usePolicyForm;
